import CommonAPIService from "@/serviceHandlers/commonHandler";
import router from "src/routes/routes";
import { geolocationService } from "@/serviceHandlers/geolocationService";

const api = new CommonAPIService(router);

// Module-level cache shared across every component instance so a given
// coordinate pair is only reverse-geocoded once per browser session. Values:
//   undefined -> never requested
//   ""        -> requested, no address resolved (show coordinates instead)
//   string    -> resolved human-readable address
const addressCache = new Map();

/**
 * Mixin for manager-facing views that display where a caregiver actually
 * clocked in/out. Geocodes are stored as raw "lat,lng" strings on the shift
 * (and per-caregiver clock times); this resolves them to a human-readable
 * address via the reverse-geocode endpoint, with a Google Maps link fallback
 * that works even when reverse geocoding is unavailable.
 */
export default {
  data() {
    return {
      // Reactive view of resolved addresses, keyed by geocode string.
      clockLocationAddresses: {},
    };
  },
  methods: {
    // Parse a "lat,lng" geocode string into { latitude, longitude } or null.
    parseClockGeocode(geocode) {
      return geolocationService.parseGeocodeString(geocode);
    },

    // A Google Maps link centered on the captured coordinates. Always works,
    // regardless of whether reverse geocoding is available.
    clockMapUrl(geocode) {
      const parsed = this.parseClockGeocode(geocode);
      if (!parsed) return null;
      return `https://www.google.com/maps/search/?api=1&query=${parsed.latitude},${parsed.longitude}`;
    },

    // Short "lat, lng" label, used as a fallback before/without an address.
    clockCoordsLabel(geocode) {
      const parsed = this.parseClockGeocode(geocode);
      if (!parsed) return "";
      return `${parsed.latitude.toFixed(5)}, ${parsed.longitude.toFixed(5)}`;
    },

    // The best available human-readable label for a geocode: the resolved
    // address if we have one, otherwise the raw coordinates.
    clockLocationLabel(geocode) {
      const resolved = this.clockLocationAddresses[geocode];
      if (resolved) return resolved;
      return this.clockCoordsLabel(geocode);
    },

    // Reverse-geocode a single geocode string and store the result reactively.
    async resolveClockLocation(geocode) {
      const parsed = this.parseClockGeocode(geocode);
      if (!parsed) return;

      if (addressCache.has(geocode)) {
        const cached = addressCache.get(geocode);
        if (cached) this.$set(this.clockLocationAddresses, geocode, cached);
        return;
      }

      try {
        const result = await api.getCall("/api/geocoding/reverse", {
          lat: parsed.latitude,
          lng: parsed.longitude,
        });
        const address = (result && result.formatted_address) || "";
        addressCache.set(geocode, address);
        if (address) this.$set(this.clockLocationAddresses, geocode, address);
      } catch (e) {
        // Reverse geocoding is best-effort — cache the miss so we don't retry
        // it repeatedly, and fall back to showing raw coordinates + map link.
        addressCache.set(geocode, "");
      }
    },

    // Resolve a batch of geocode strings (ignoring blanks/duplicates).
    resolveClockLocations(geocodes) {
      const unique = [...new Set((geocodes || []).filter(Boolean))];
      unique.forEach((g) => this.resolveClockLocation(g));
    },
  },
};
